import { ArrowRight, CornerDownLeft } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { useUi } from '../store/ui';
import { NAV, PHASE_TITLES } from '../routes/nav';

/**
 * Ctrl/Cmd+K launcher. Fuzzy-filters navigation by label and shows which phase
 * a locked screen arrives in, so the roadmap is discoverable from anywhere.
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const user = useAuth((s) => s.user);
  const theme = useUi((s) => s.theme);
  const setTheme = useUi((s) => s.setTheme);
  const setDirection = useUi((s) => s.setDirection);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const commands = useMemo(() => {
    const nav = NAV
      .filter((n) => user?.permissions.includes(n.permission))
      .map((n) => ({
        id: `nav:${n.to}`,
        label: n.label,
        hint: n.phase > 1 ? PHASE_TITLES[n.phase] : n.section,
        run: () => navigate(n.to),
      }));
    const actions = [
      { id: 'theme:toggle', label: theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme', hint: 'Appearance', run: () => setTheme(theme === 'dark' ? 'light' : 'dark') },
      { id: 'dir:toggle', label: 'Toggle right-to-left layout', hint: 'Appearance · Arabic/Hebrew', run: () => setDirection('rtl') },
      { id: 'auth:out', label: 'Sign out', hint: 'Session', run: () => useAuth.getState().signOut() },
    ];
    return [...nav, ...actions];
  }, [user, navigate, theme, setTheme, setDirection]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) => c.label.toLowerCase().includes(q) || c.hint.toLowerCase().includes(q));
  }, [commands, query]);

  useEffect(() => { setActive(0); }, [query]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
      if (e.key === 'Enter') { e.preventDefault(); filtered[active]?.run(); onClose(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, filtered, active, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 pt-[12vh]">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div role="dialog" aria-modal="true" aria-label="Command palette" className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-line bg-surface-1 shadow-pop animate-fade-in">
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Jump to a screen or run a command…"
          aria-label="Search commands"
          className="h-14 w-full border-b border-line bg-transparent px-4 text-base text-ink outline-none placeholder:text-ink-faint"
        />
        <ul className="max-h-[50vh] overflow-y-auto p-2" role="listbox">
          {filtered.length === 0 && <li className="px-3 py-6 text-center text-sm text-ink-muted">No matches</li>}
          {filtered.map((c, i) => (
            <li key={c.id} role="option" aria-selected={i === active}>
              <button
                onMouseEnter={() => setActive(i)}
                onClick={() => { c.run(); onClose(); }}
                className={`flex min-h-touch w-full items-center gap-3 rounded-lg px-3 py-2 text-start text-sm ${
                  i === active ? 'bg-brand-50 text-brand-800 dark:bg-brand-900/30 dark:text-brand-200' : 'text-ink'
                }`}
              >
                <ArrowRight className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
                <span className="flex-1 truncate">{c.label}</span>
                <span className="shrink-0 text-xs text-ink-faint">{c.hint}</span>
                {i === active && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-ink-faint" aria-hidden />}
              </button>
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-4 border-t border-line px-4 py-2 text-[11px] text-ink-faint">
          <span>↑↓ to navigate</span><span>↵ to open</span><span>Esc to close</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
